import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, LinkIcon, Plus, X, ExternalLink, Phone, Mail, MessageSquare, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { useContextualTimeZone } from '@/components/TimezoneProvider';
import { formatInTimeZone } from 'date-fns-tz';
import { EnumAlertStatus, addAlertNote } from '../alertApi';
import { format } from 'date-fns';
import { Time } from '@/components/ui/time';

import { IntruderDetailsSection } from './IntruderDetailsSection';
import { useAuthStore } from '@/store/authStore';
import { parseDateWithoutTimezone, getInvolvedHumans } from '../utils';
import { MonitorAlertFullDto, ContactEventDto } from '@/app/Alert/types';
import { IncidentVideoSection } from './IncidentVideoSection';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { FeatureFlags } from '@/lib/feature-flag';
import { formatNoteForAdminDetails } from '@/lib/tagging';

interface EvidenceLink {
  id: string;
  evidenceId: number;
  url: string;
  title: string;
  description: string;
  author: string;
  date: Date;
}

interface AdminAlertDetailsProps {
  alert: MonitorAlertFullDto;
  refetchAlert: () => Promise<unknown>;
}

// Format note text: strip [TAG:uuid], [UNTAG:uuid], and [EVIDENCE] prefixes, clean up display
// Uses shared utility from @/lib/tagging
const formatNoteText = formatNoteForAdminDetails;

function getContactEventLabel(event: ContactEventDto): string {
  switch (event.type) {
    case 'MonitoringNotified': return 'Notification';
    case 'ContactSms': return `SMS to ${event.to || 'Unknown'}`;
    case 'ContactSmsReply': return `SMS from ${event.to || 'Unknown'}`;
    case 'ContactCall': return `Call to ${event.to || 'Unknown'}`;
    case 'ContactEmail': return `Email to ${event.to || 'Unknown'}`;
    case 'Police': return 'Police';
    default: return 'Contact';
  }
}

function getContactEventIcon(event: ContactEventDto) {
  switch (event.type) {
    case 'ContactSms':
    case 'ContactSmsReply':
      return MessageSquare;
    case 'ContactCall':
      return Phone;
    case 'ContactEmail':
      return Mail;
    default:
      return Bell;
  }
}

// Parse evidence content fields (URL, Title, Description) from note body
function parseEvidenceContent(content: string): { url: string; title: string; description: string } {
  const lines = content.split('\n');
  
  let url = '';
  let title = '';
  let description = '';
  
  for (const line of lines) {
    if (line.startsWith('URL:')) {
      url = line.replace('URL:', '').trim();
    } else if (line.startsWith('Title:')) {
      title = line.replace('Title:', '').trim();
    } else if (line.startsWith('Description:')) {
      description = line.replace('Description:', '').trim();
    }
  }
  
  return { url, title, description };
}

function groupNotesByDate(
  notes: MonitorAlertFullDto['recentNotes'],
  timeZone: string
): Record<string, NonNullable<MonitorAlertFullDto['recentNotes']>> {
  return (notes || []).reduce(
    (groups, note) => {
      if (!note.date) return groups;
      const timeZoneAdjustedDateKey = formatInTimeZone(note.date, timeZone, 'yyyy-MM-dd');
      if (!groups[timeZoneAdjustedDateKey]) {
        groups[timeZoneAdjustedDateKey] = [];
      }
      groups[timeZoneAdjustedDateKey].push(note);
      return groups;
    },
    {} as Record<string, NonNullable<MonitorAlertFullDto['recentNotes']>>
  );
}

export function AdminAlertDetails({
  alert,
  refetchAlert,
}: AdminAlertDetailsProps) {
  const user = useAuthStore((state) => state.user);
  const userFullName = user ? `${user.firstName} ${user.lastName}`.trim() : 'Agent';
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const taggingEnabled = useFeatureFlagEnabled(FeatureFlags.Tagging);
  const [notesPage, setNotesPage] = useState(0);
  const [notesPageSize] = useState<number | 'all'>(20);

  const startTimeOverride = useMemo(() => {
    if (!alert?.recentNotes) return null;
    const sorted = [...alert.recentNotes].sort(
      (a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime()
    );
    for (const note of sorted) {
      const text = note.note || '';
      if (text.startsWith('[START_TIME_OVERRIDE]')) {
        const parsed = new Date(text.replace('[START_TIME_OVERRIDE]', '').trim());
        if (!isNaN(parsed.getTime())) return parsed;
      }
    }
    return null;
  }, [alert?.recentNotes]);

  const effectiveStartTime = startTimeOverride || alert.occurred;
  
  // Evidence link form state
  const [evidenceUrl, setEvidenceUrl] = useState('');
  const [evidenceTitle, setEvidenceTitle] = useState('');
  const [evidenceDescription, setEvidenceDescription] = useState('');
  
  // URL validation
  const isValidUrl = (url: string): boolean => {
    if (!url.trim()) return true; // Empty is not invalid, just incomplete
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };
  const urlIsValid = isValidUrl(evidenceUrl);
  const showUrlError = evidenceUrl.trim() !== '' && !urlIsValid;

  interface ActivityEntry {
    type: 'note' | 'contact';
    date: Date | string;
    author?: string;
    note?: string;
    contactEvent?: ContactEventDto;
  }

  const allActivityEntries = useMemo(() => {
    const entries: ActivityEntry[] = [];
    if (alert?.recentNotes) {
      for (const note of alert.recentNotes) {
        entries.push({
          type: 'note',
          date: note.date || '',
          author: note.author,
          note: note.note,
        });
      }
    }
    if (alert?.contactEvents) {
      for (const event of alert.contactEvents) {
        entries.push({
          type: 'contact',
          date: event.occurred || '',
          author: getContactEventLabel(event),
          contactEvent: event,
        });
      }
    }
    return entries.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [alert?.recentNotes, alert?.contactEvents]);

  const paginatedActivity =
    notesPageSize === 'all'
      ? allActivityEntries
      : allActivityEntries.slice(0, (notesPage + 1) * notesPageSize);

  const totalActivityEntries = allActivityEntries.length;
  const hasMoreEntries = totalActivityEntries > paginatedActivity.length;

  const { timeZone, getUTCOffset } = useContextualTimeZone();

  // Parse evidence links from notes, assigning incremental IDs
  const evidenceData = useMemo(() => {
    if (!alert?.recentNotes) return { links: [] as EvidenceLink[], nextId: 1 };
    
    const linkMap = new Map<number, EvidenceLink>();
    const removedIds = new Set<number>();
    let nextId = 1;
    
    // Sort notes by date (oldest first) to process in order
    const sortedNotes = [...alert.recentNotes].sort(
      (a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime()
    );
    
    for (const note of sortedNotes) {
      const noteText = note.note || '';
      
      // Check for REMOVE_EVIDENCE_N (new format)
      const removeIdMatch = noteText.match(/^\[REMOVE_EVIDENCE_(\d+)\]/);
      if (removeIdMatch) {
        const id = parseInt(removeIdMatch[1], 10);
        removedIds.add(id);
        linkMap.delete(id);
        continue;
      }
      
      // Check for REMOVE_EVIDENCE:url (legacy format)
      const removeUrlMatch = noteText.match(/^\[REMOVE_EVIDENCE:([^\]]+)\]/);
      if (removeUrlMatch) {
        const url = removeUrlMatch[1];
        for (const [key, link] of linkMap) {
          if (link.url === url) {
            removedIds.add(key);
            linkMap.delete(key);
          }
        }
        continue;
      }
      
      // Check for EVIDENCE_N (new format)
      const evidenceIdMatch = noteText.match(/^\[EVIDENCE_(\d+)\]/);
      if (evidenceIdMatch) {
        const evidenceId = parseInt(evidenceIdMatch[1], 10);
        nextId = Math.max(nextId, evidenceId + 1);
        if (!removedIds.has(evidenceId)) {
          const content = noteText.replace(/^\[EVIDENCE_\d+\]/, '').trim();
          const parsed = parseEvidenceContent(content);
          if (parsed.url) {
            linkMap.set(evidenceId, {
              id: `evidence-${evidenceId}`,
              evidenceId,
              ...parsed,
              author: note.author || 'Unknown',
              date: new Date(note.date || 0),
            });
          }
        }
        continue;
      }
      
      // Check for EVIDENCE (legacy format without ID)
      if (noteText.startsWith('[EVIDENCE]')) {
        const evidenceId = nextId++;
        if (!removedIds.has(evidenceId)) {
          const content = noteText.replace('[EVIDENCE]', '').trim();
          const parsed = parseEvidenceContent(content);
          if (parsed.url) {
            linkMap.set(evidenceId, {
              id: `evidence-${evidenceId}`,
              evidenceId,
              ...parsed,
              author: note.author || 'Unknown',
              date: new Date(note.date || 0),
            });
          }
        }
        continue;
      }
    }
    
    return { links: Array.from(linkMap.values()), nextId };
  }, [alert?.recentNotes]);

  // Add evidence link mutation
  const addEvidenceMutation = useMutation({
    mutationFn: (noteText: string) => {
      if (!alert.id) {
        throw new Error('Missing alert data');
      }
      return addAlertNote(alert.id, {
        note: noteText,
        author: userFullName,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fullAlert', alert.id] });
      queryClient.invalidateQueries({ queryKey: ['alert', alert.id] });
      toast({ title: 'Evidence link added' });
      setEvidenceUrl('');
      setEvidenceTitle('');
      setEvidenceDescription('');
    },
    onError: () => {
      toast({ title: 'Failed to add evidence link', variant: 'destructive' });
    },
  });

  // Remove evidence link mutation
  const removeEvidenceMutation = useMutation({
    mutationFn: (evidenceId: number) => {
      if (!alert.id) {
        throw new Error('Missing alert data');
      }
      return addAlertNote(alert.id, {
        note: `[REMOVE_EVIDENCE_${evidenceId}] Removed evidence link`,
        author: userFullName,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fullAlert', alert.id] });
      queryClient.invalidateQueries({ queryKey: ['alert', alert.id] });
      toast({ title: 'Evidence link removed' });
    },
    onError: () => {
      toast({ title: 'Failed to remove evidence link', variant: 'destructive' });
    },
  });

  const handleAddEvidence = () => {
    if (!evidenceUrl.trim()) return;
    
    const noteLines = [`[EVIDENCE_${evidenceData.nextId}]`, `URL: ${evidenceUrl.trim()}`];
    if (evidenceTitle.trim()) {
      noteLines.push(`Title: ${evidenceTitle.trim()}`);
    }
    if (evidenceDescription.trim()) {
      noteLines.push(`Description: ${evidenceDescription.trim()}`);
    }
    
    addEvidenceMutation.mutate(noteLines.join('\n'));
  };

  return (
    <div 
      className="grid gap-2 overflow-y-scroll border-l">
      
      <Accordion
        type="multiple"
        defaultValue={['alert-details', 'notes', 'events']}
        className="w-full"
      ><div className='h-[56px] flex items-center px-4 py-3 sticky top-0 border-b bg-[#1b1b1b] z-20 gap-2'> <Clock className="h-4 w-4" /> 
      <p className="text-xs font-normal text-gray-400">
      All Times shown are <br/><span className='font-semibold text-white'> {timeZone} ({getUTCOffset()})</span>
      </p>
    </div>
          <AccordionItem value="alert-details" data-onboarding="admin-alert-details">
            <AccordionTrigger className="p-4 font-semibold">Alert Details</AccordionTrigger>
            <AccordionContent className="border-t text-xs">
              <div className="p-4">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-3 ">
                    <div className="">
                      <label className="font-semibold">Occurred</label>
                      <p className="font-mono text-gray-400 mt-0.5 "><Time date={effectiveStartTime} format="MMM d, yyyy, hh:mm a" fallback="N/A" /></p>
                    </div>
                    <div className="">
                      <label className="text-md font-semibold">Notified </label>
                      <p className="text-md font-mono text-gray-400 mt-0.5"><Time date={alert.notifiedAt} format="MMM d, yyyy, hh:mm a" fallback="N/A" /></p>
                    </div>
                    <div className="">
                      <label className="text-md font-semibold">Actioned </label>
                      <p className="text-md font-mono text-gray-400 mt-0.5"><Time date={alert.actionedAt} format="MMM d, yyyy, hh:mm a" fallback="N/A" /></p>
                    </div>
                    <div className="">
                      <label className="text-md font-semibold">Closed </label>
                      <p className="text-md font-mono text-gray-400 mt-0.5"><Time date={alert.closedAt} format="MMM d, yyyy, hh:mm a" fallback="N/A" /></p>
                    </div>
                    {getInvolvedHumans(alert).length > 0 && (
                      <div className="">
                        <label className="text-md font-semibold">Reviewed By</label>
                        <p className="text-md">{getInvolvedHumans(alert).join(', ')}</p>
                      </div>
                    )}
                    {alert.disarmedAt && (
                      <div className="">
                        <label className="text-md font-semibold">Disarmed At</label>
                        <p className="text-md"><Time date={alert.disarmedAt} format="MMM d, yyyy, hh:mm a" /></p>
                      </div>
                    )}
                   
                  </div>
                  <div className="space-y-4">
                  <div className="">
                      <label className="font-semibold">Status</label>
                      <p className="">{alert.status}</p>
                    </div>
                    <div className="">
                      <label className="text-md font-semibold">Company</label>
                      <div className="flex items-center gap-2">
                        <p className="text-md">{alert.companyName}</p>
                        <Link to={`/customers/${alert.companyId}`}>
                          <Button variant="ghost" size="icon" className="h-6 w-6 ">
                            {/* <LinkIcon className="h-4 w-4" /> */}
                          </Button>
                        </Link>
                      </div>
                    </div>
                    <div className="">
                      <label className="text-md font-semibold">Site</label>
                      <Link to={`/Customer/${alert.companyId}/project/${alert.projectId}`} ><div className="flex items-center "> 
                        <p className="text-md">{alert.projectName}</p>
                       
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-lime">
                            <LinkIcon className="h-4 w-4" />
                          </Button> </div>
                        </Link>
                     
                    </div>
                  </div>
                
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        {alert.status === EnumAlertStatus.Intruder && (
          <AccordionItem value="intruder-details" data-onboarding="admin-alert-intruder-details">
            <AccordionTrigger className="p-4 font-semibold">Intruder Details</AccordionTrigger>
            <AccordionContent className="border-t">
              <div className="p-6">
                <IntruderDetailsSection alert={alert} onSaved={refetchAlert} />
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {alert.intruderEventData?.videoClip && (
          <IncidentVideoSection videoClip={alert.intruderEventData.videoClip} />
        )}

        {taggingEnabled && (
          <AccordionItem value="evidence-links" data-onboarding="admin-alert-evidence">
            <AccordionTrigger className="p-4 font-semibold">
              <div className="flex w-full items-center justify-between pr-4">
                <span>Additional Evidence</span>
                {evidenceData.links.length > 0 && (
                  <span className="text-xs text-gray-400">({evidenceData.links.length})</span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="border-t">
              <div className="p-4 space-y-4">
                {/* Inline add evidence form */}
                <div className="space-y-3 rounded-md border border-dashed border-gray-600 p-3">
                  <p className="font-medium text-gray-300">Add Evidence Link</p>
                  <div className="space-y-2">
                    <div>
                      <Input
                        value={evidenceUrl}
                        onChange={(e) => setEvidenceUrl(e.target.value)}
                        placeholder="URL (required)"
                        className={`${showUrlError ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                      />
                      {showUrlError && (
                        <p className="text-xs text-red-500 mt-1">Please enter a valid URL (e.g., https://example.com)</p>
                      )}
                    </div>
                    <Input
                      value={evidenceTitle}
                      onChange={(e) => setEvidenceTitle(e.target.value)}
                      placeholder="Title (optional)"
                      className=""
                    />
                    <Input
                      value={evidenceDescription}
                      onChange={(e) => setEvidenceDescription(e.target.value)}
                      placeholder="Description (optional)"
                      className=""
                    />
                  </div>
                  <Button
                    onClick={handleAddEvidence}
                    disabled={!evidenceUrl.trim() || !urlIsValid || addEvidenceMutation.isPending}
                    size="sm"
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {addEvidenceMutation.isPending ? 'Adding...' : 'Add Link'}
                  </Button>
                </div>
                {/* Existing evidence links list */}
                {evidenceData.links.length > 0 && (
                  <div className="space-y-2">
                    {evidenceData.links.map((link) => (
                      <div key={link.id} className="flex items-start gap-3 rounded-md border bg-[#1b1b1b] p-3">
                        <ExternalLink className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          {link.title && (
                            <p className="font-semibold ">{link.title}</p>
                          )}
                          <a 
                            href={link.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:underline break-all"
                          >
                            {link.url}
                          </a>
                          {link.description && (
                            <p className="text-gray-400 mt-1">{link.description}</p>
                          )}
                          <p className="text-xs text-gray-500 mt-2">
                            Added by {link.author}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-gray-400 hover:text-red-400"
                          onClick={() => removeEvidenceMutation.mutate(link.evidenceId)}
                          disabled={removeEvidenceMutation.isPending}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                
              </div>
            </AccordionContent>
          </AccordionItem>
        )}
       
        <AccordionItem value="notes" data-onboarding="admin-alert-notes">
          <AccordionTrigger className="p-4 font-semibold">
            <div className="flex w-full items-center justify-between pr-4">
              <span>Activity Log</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="">
            
            <div className="">
              {allActivityEntries.length > 0 ? (
                <>
                  <div className="relative flex flex-col">
                    <div
                      className="absolute left-[1.6rem] top-[20px] w-[1px] bg-border"
                      style={{
                        bottom: '60px',
                      }}
                    />
                    {(() => {
                      const filteredEntries = paginatedActivity.filter((entry) => {
                        if (entry.type === 'note') {
                          return !entry.note?.match(/^\[EVIDENCE(?:_\d+)?\]/) && !entry.note?.match(/^\[REMOVE_EVIDENCE[_:]/) && !entry.note?.startsWith('[REPORT_CONFIG]');
                        }
                        return true;
                      });
                      
                      const groupedEntries = filteredEntries.reduce(
                        (groups, entry) => {
                          if (!entry.date) return groups;
                          const dateKey = formatInTimeZone(entry.date, timeZone ?? '', 'yyyy-MM-dd');
                          if (!groups[dateKey]) {
                            groups[dateKey] = [];
                          }
                          groups[dateKey].push(entry);
                          return groups;
                        },
                        {} as Record<string, ActivityEntry[]>
                      );

                      return Object.entries(groupedEntries)
                        .sort(([a], [b]) => b.localeCompare(a))
                        .map(([dateKey, dayEntries]) => (
                          <div key={dateKey} className=''>
                            <div className="sticky top-0 z-10 border-y  bg-[#1b1b1b] p-4">
                              <h3 className="text-xs font-semibold text-gray-300">
                              {format(parseDateWithoutTimezone(dateKey), 'EEEE, MMMM d, yyyy')}
                              </h3>
                            </div>

                            {dayEntries.map((entry, index) => {
                              const IconComponent = entry.type === 'contact' && entry.contactEvent
                                ? getContactEventIcon(entry.contactEvent)
                                : Calendar;
                              return (
                              <div
                                key={`${dateKey}-${index}`}
                                className="relative py-3 pl-12 pr-4 text-[0.8rem]"
                              >
                                <div className="absolute left-0 top-5 flex items-center gap-0">
                                  <div className="z-2 ml-[0.75rem] rounded-md border bg-[#1b1b1b] p-1">
                                    <IconComponent className="h-4 w-4 text-gray-400" />
                                  </div>
                                  <div className="ml-2.5 flex items-center rounded-md border bg-[#2E3835] px-2 py-1 text-xs font-semibold shadow-sm">
                                    {entry.author || 'Unknown'}
                                  </div>
                                  <p className="ml-1.5 text-xs font-normal text-gray-500">
                                    <Time date={entry.date} format="h:mm a" fallback="Unknown time" noTooltip />
                                  </p>
                                </div>
                                <div className="mt-10 flex w-full">
                                  <div className="w-full flex-1">
                                    <div className="mb-0 text-gray-300">
                                      {entry.type === 'contact' && entry.contactEvent
                                        ? (entry.contactEvent.body || '')
                                            .split('\n')
                                            .map((line: string, lineIndex: number) => (
                                              <div key={lineIndex}>{line}</div>
                                            ))
                                        : formatNoteText(entry.note || '')
                                            .split('\n')
                                            .map((line: string, lineIndex: number) => (
                                              <div key={lineIndex}>{line}</div>
                                            ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        ));
                    })()}
                  </div>
                  {hasMoreEntries && (
                    <div className="flex items-center justify-center px-6 py-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setNotesPage((p) => p + 1)}
                      >
                        Show more ({totalActivityEntries - paginatedActivity.length} remaining)
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <p className="italic text-gray-500">No activity on this alert</p>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="contact-log" data-onboarding="contact-log">
          <AccordionTrigger className="p-4 font-semibold">
            <div className="flex w-full items-center justify-between pr-4">
              <span>Contact Log</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="">
            <div className="">
              {alert.contactEvents && alert.contactEvents.length > 0 ? (
                <div className="relative flex flex-col">
                  <div
                    className="absolute left-[1.6rem] top-[20px] w-[1px] bg-border"
                    style={{ bottom: '60px' }}
                  />
                  {(() => {
                    const groupedEvents = alert.contactEvents.reduce(
                      (groups, event) => {
                        if (!event.occurred) return groups;
                        const dateKey = formatInTimeZone(event.occurred, timeZone ?? '', 'yyyy-MM-dd');
                        if (!groups[dateKey]) {
                          groups[dateKey] = [];
                        }
                        groups[dateKey].push(event);
                        return groups;
                      },
                      {} as Record<string, ContactEventDto[]>
                    );

                    return Object.entries(groupedEvents)
                      .sort(([a], [b]) => b.localeCompare(a))
                      .map(([dateKey, dayEvents]) => (
                        <div key={dateKey}>
                          <div className="sticky top-0 z-10 border-y bg-[#1b1b1b] p-4">
                            <h3 className="text-xs font-semibold text-gray-300">
                              {format(parseDateWithoutTimezone(dateKey), 'EEEE, MMMM d, yyyy')}
                            </h3>
                          </div>

                          {dayEvents.map((event, index) => {
                            const IconComponent = getContactEventIcon(event);
                            return (
                              <div
                                key={`${dateKey}-${index}`}
                                className="relative py-3 pl-12 pr-4 text-[0.8rem]"
                              >
                                <div className="absolute left-0 top-5 flex items-center gap-0">
                                  <div className="z-2 ml-[0.75rem] rounded-md border bg-[#1b1b1b] p-1">
                                    <IconComponent className="h-4 w-4 text-gray-400" />
                                  </div>
                                  <div className="ml-2.5 flex items-center rounded-md border bg-[#2E3835] px-2 py-1 text-xs font-semibold shadow-sm">
                                    {getContactEventLabel(event)}
                                  </div>
                                  <p className="ml-1.5 text-xs font-normal text-gray-500">
                                    <Time date={event.occurred} format="h:mm a" fallback="Unknown time" noTooltip />
                                  </p>
                                </div>
                                <div className="mt-10 flex w-full">
                                  <div className="w-full flex-1">
                                    <div className="mb-0 text-gray-300">
                                      {(event.body || '')
                                        .split('\n')
                                        .map((line: string, lineIndex: number) => (
                                          <div key={lineIndex}>{line}</div>
                                        ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ));
                  })()}
                </div>
              ) : (
                <p className="px-4 py-2 italic text-gray-500">No contact events on this alert</p>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
